import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { screen } from '@/components/common/breakpoints';
import { Icon } from '@/components/common/Icon';
import { bodyMediumSemiBold } from '@/styles/typography';

import type { IconSet } from '@/components/common/Icon';
import type { SerializedStyles } from '@emotion/react';
import type { AnchorHTMLAttributes } from 'react';
import type { UrlObject } from 'url';

export const CoreStyling = css`
  ${bodyMediumSemiBold};
  text-decoration: none;
  max-width: 100%;
  width: max-content;
  min-height: 40px;
  font-size: var(--font-size-x-small);
  border-radius: var(--radius-full);
  padding: var(--spacing-x-small) var(--spacing-small);
  display: inline-flex;
  position: relative;
  align-items: center;

  &.disabled,
  &:disabled {
    opacity: var(--opacity-disabled);
    pointer-events: none;
  }

  ${screen.sm} {
    padding: var(--spacing-x-small) var(--spacing-medium);
    min-height: 48px;
  }
  ${screen.md} {
    padding: var(--spacing-small) var(--spacing-large);
    min-height: 56px;
  }
`;

type CoreButton = {
  componentTheme: 'light' | 'dark';
};

type Button = CoreButton & React.ButtonHTMLAttributes<HTMLButtonElement>;

export type ButtonLink = CoreButton &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
    href: UrlObject | string;
  };

export type ButtonProps = Button | ButtonLink;

export type IconButtonProps = {
  componentTheme: 'light' | 'dark';
  icon?: IconSet;
  iconSize?: 'normal' | 'large';
  variant?: 'primary' | 'secondary' | 'tertiary';
  iconOnly?: boolean;
  className?: string;
};

export const IconButton = ({
  componentTheme,
  icon = 'ArrowRight',
  variant = 'primary',
  iconOnly = true,
  iconSize = 'normal',
  ...props
}: IconButtonProps) => {
  const getButtonStyles = () => {
    // !iconOnly is only a possibility for the primary button
    // the colours need to be reversed as the icon colours needs to be the opposite of the button colour
    if (!iconOnly) {
      if (componentTheme === 'light')
        return css`
          background: var(--surface-subtle);
          svg {
            color: var(--icon-action);
          }
        `;
      if (componentTheme === 'dark')
        return css`
          background: var(--surface-primary);
          svg {
            color: var(--icon-inverse-strong);
          }
        `;
    }

    switch (variant) {
      case 'primary':
        if (componentTheme === 'light')
          return css`
            background: var(--surface-primary);
            &:hover {
              background: var(--surface-inverse-primary);
            }
            svg {
              color: var(--icon-inverse-strong);
            }
          `;
        if (componentTheme === 'dark')
          return css`
            background: var(--surface-subtle);
            svg {
              color: var(--icon-action);
            }
            &:hover {
              svg {
                color: var(--icon-dark-action);
              }
            }
          `;
        break;
      case 'secondary':
        if (componentTheme === 'light')
          return css`
            background: var(--transparent);
            border: 2px solid var(--border-action);
            svg {
              color: var(--icon-action);
            }
            &:hover {
              background: var(--surface-primary-subtle);
              border: 2px solid var(--border-action-dark);
              svg {
                color: var(--icon-action-dark);
              }
            }
          `;
        if (componentTheme === 'dark')
          return css`
            background: var(--transparent);
            border: 2px solid var(--border-inverse-strong);
            svg {
              color: var(--icon-inverse-strong);
            }
            &:hover {
              background: var(--surface-inverse-primary);
            }
          `;
        break;
      case 'tertiary':
        if (componentTheme === 'light')
          return css`
            background: var(--transparent);
            svg {
              color: var(--icon-action);
            }
            &:hover {
              background: var(--surface-primary-subtle);
              svg {
                color: var(--icon-action-dark);
              }
            }
          `;
        if (componentTheme === 'dark')
          return css`
            background: var(--transparent);
            svg {
              color: var(--icon-inverse-strong);
            }
            &:hover {
              background: var(--surface-inverse-primary);
            }
          `;
        break;
      default:
        return '';
    }
  };

  return (
    <IconBackground buttonStyles={getButtonStyles()} {...props}>
      <StyledIcon icon={icon} size={iconSize === 'large' ? '24px' : '16px'} />
    </IconBackground>
  );
};

const StyledIcon = styled(Icon)`
  align-self: center;

  // Being able to set a icon size bigger than this looks weird at very small button sizes
  max-width: 20px;

  ${screen.sm} {
    max-width: unset;
  }
`;

const IconBackground = styled.div<{ buttonStyles: SerializedStyles | string }>`
  ${({ buttonStyles }) => buttonStyles}

  height: 32px;
  width: 32px;
  border-radius: var(--radius-full);
  transition-duration: 400ms;
  transition-property: background, color;
  display: flex;
  justify-content: center;

  ${screen.sm} {
    height: 40px;
    width: 40px;
  }

  ${screen.md} {
    height: 44px;
    width: 44px;
  }
`;

export const createRipple = (event) => {
  const button = event.currentTarget;

  const circle = document.createElement('span');
  const diameter = Math.max(button.clientWidth, button.clientHeight);
  const radius = diameter / 2;
  const clientRect = event.currentTarget.getBoundingClientRect();

  circle.style.width = circle.style.height = `${diameter}px`;
  circle.style.left = `${event.clientX - clientRect.left - radius}px`;
  circle.style.top = `${event.clientY - clientRect.top - radius}px`;
  circle.classList.add('ripple');

  const ripple = button.getElementsByClassName('ripple')[0];

  if (ripple) {
    ripple.remove();
  }

  button.appendChild(circle);
};

export const RippleAnimation = (
  themeType: 'primary' | 'secondary' | 'tertiary',
) => {
  const rippleColour =
    themeType === 'primary'
      ? '--decorative-starlight'
      : '--decorative-soft-galaxy';
  return css`
    position: relative;
    overflow: hidden;
    transition-duration: 400ms;
    transition-property: background, color;

    .ripple {
      background-color: var(${rippleColour});
      position: absolute;
      border-radius: 50%;
      transform: scale(0);
      animation: ripple 600ms linear;
      z-index: 1;
    }

    @keyframes ripple {
      to {
        transform: scale(4);
        opacity: 0;
      }
    }
  `;
};
